import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';

import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import {
  isAfter,
  parse as parseDate,
  startOfDay,
  parseISO,
  isValid,
} from 'date-fns';

import { useImmer } from 'use-immer';
import { isEqual, sortBy } from 'lodash';
import swal from 'sweetalert2';
import { featureEnabled, formatCurrency } from 'utils';
import moment from 'moment';

import errorReporter from '@app/errorReporter';
import { BuildJsonType, OrgFeatures } from '@bb/domain/dist/types';

import { getOnboardingPortalAssociationByPortalId } from 'api/OnboardingPortalController';
import { FileDoc } from '@bb/domain/dist/schemas';
import { GetListingFilesResponse } from 'api/FileController/list';
import { Listing } from 'api/ListingController/getListing';
import message from '../components/Message';
import theme from '../components/theme';
import { AuthContext, ListingLabelContext } from '../contexts';
import {
  ListingFormState,
  ListingType,
  PhotoFileType,
  AvailabilityDetailsType,
  AttachmentsType,
  UpdateFileType,
  SaveListingType,
} from '../Router/pages/ListingPages/types';

type UseListingReturnObject = {
  listing: ListingFormState;
  setListing: React.Dispatch<React.SetStateAction<ListingFormState>>;
  initialListing: ListingFormState;
  photos: PhotoFileType[];
  setPhotos: React.Dispatch<React.SetStateAction<PhotoFileType[]>>;
  setDeletedPhotos: React.Dispatch<React.SetStateAction<PhotoFileType[]>>;
  thumbnail: PhotoFileType | null;
  setThumbnail: React.Dispatch<React.SetStateAction<PhotoFileType | null>>;
  attachments: AttachmentsType;
  updateFile: UpdateFileType;
  isListingModified: boolean;
  isListingDeletedOrRestored: boolean;
  loading: boolean;
  uploading: boolean;
  saveListing: SaveListingType;
  distributeListing: () => Promise<void>;
  submitListingForReview: () => Promise<void>;
  deleteListing: () => Promise<void>;
  restoreListing: () => Promise<void>;
  saved: boolean;
  listingCreated: boolean;
  isFormValid: boolean;
  errorMessage: string;
  formFieldsMissing: boolean;
  openHousesErrors: boolean[];
  validAvailabilities: ListingType['availability'][];
  availabilityDetails: AvailabilityDetailsType;
  setAvailabilityDetails: React.Dispatch<
    React.SetStateAction<AvailabilityDetailsType>
  >;
  initialAvailabilityDetails: AvailabilityDetailsType;
  documents: any[];
  setDocuments: React.Dispatch<React.SetStateAction<any[]>>;
  hasFeature: (feature: OrgFeatures) => boolean;
  hasNarSettlement: boolean;
  hasNarBuyerSettlement: boolean;
};

type UseListingType = (listingId?: string) => UseListingReturnObject;

const emptyListing: ListingFormState = {
  id: undefined,
  type: undefined,
  subtype: undefined,
  createdBy: undefined,
  location: undefined,
  locations: undefined,
  isDeleted: undefined,
  org: undefined,
  line1: undefined,
  line2: undefined,
  country: undefined,
  province: undefined,
  city: undefined,
  neighborhood: undefined,
  postal_code: undefined,
  list_price: undefined,
  list_price_type: undefined,
  expiry: undefined,
  commission: undefined,
  status: undefined,
  coming_soon_date: undefined,
  reps: [],

  virtualTourUrl: undefined,

  client_remarks: undefined,
  brokerage_remarks: undefined,

  dir_cross: undefined,
  fronts_on: undefined,
  taxes: undefined,
  possession: undefined,
  occupancy: undefined,
  legal_description: undefined,

  openHouses: [],

  total_rooms: undefined,
  bedrooms: undefined,
  bathrooms: undefined,
  rooms_plus: undefined,
  bedrooms_plus: undefined,
  kitchens: undefined,
  basement: undefined,
  laundry_access: undefined,
  heat_source: undefined,
  heat_type: undefined,
  ac: undefined,
  age: undefined,
  apx_sqft: undefined,
  sqft: undefined,
  water: undefined,
  utilities: undefined,

  exteriorType: undefined,
  style: undefined,
  exterior: undefined,
  lot_front: undefined,
  lot_depth: undefined,
  garage_type: undefined,
  garage_spaces: undefined,
  drive: undefined,
  parking_spaces: undefined,
  parking_cost: undefined,
  parking_included: undefined,
  private_entrance: undefined,

  rooms: [],
  required: {
    lease_term: undefined,
    payment_method: undefined,
    rental_application: undefined,
    deposit: undefined,
    credit_check: undefined,
    employment_letter: undefined,
  },
  freehold: {
    included_excluded: {
      all_inclusive: undefined,
      cable: undefined,
      cac: undefined,
      gas: undefined,
      heat: undefined,
      hydro: undefined,
      water: undefined,
      phone: undefined,
    },
  },

  holdover: undefined,
  condo: {
    level: undefined,
    unit: undefined,
    exposure: undefined,
    building_name: undefined,
    property_management: undefined,
    balcony: undefined,
    type: undefined,
    style: undefined,
    parking_spaces: undefined,
    parking_spot: undefined,
    locker: undefined,
    ensuiteLaundry: undefined,
    laundryAccess: undefined,
    pets_allowed: undefined,
    maintenance: undefined,
    maintenance_option: {
      heat: undefined,
      hydro: undefined,
      water: undefined,
      cable: undefined,
      cac: undefined,
      building_ins: undefined,
      parking: undefined,
    },
  },

  more_info: {
    freestanding: undefined,
    industrial: {
      length: undefined,
      total: undefined,
      width: undefined,
    },
    office: {
      length: undefined,
      total: undefined,
      width: undefined,
    },
    retail: {
      length: undefined,
      total: undefined,
      width: undefined,
    },
    total_area: {
      length: undefined,
      total: undefined,
      width: undefined,
    },
    zoning: undefined,
  },

  financials: {
    taxes: undefined,
    gross_income: undefined,
    insurance: undefined,
    vacancy_allowance: undefined,
    management: undefined,
    operating_expenses: undefined,
    maintenance: undefined,
    net_income: undefined,
    heat: undefined,
    expenses: undefined,
    hydro: undefined,
    year_expenses: undefined,
    water: undefined,
    cap_rate: undefined,
    available: undefined,
  },

  visibility: 'private',
  pageStatus: undefined,
  canEdit: undefined,
};

const initialAvailabilityDetails: AvailabilityDetailsType = {
  newAvailability: undefined,
  closed_date: undefined,
  suspended_date: undefined,
  sold_date: undefined,
  leased_date: undefined,
  leased_term: undefined,
  price: undefined,
  coop_brokerage: undefined,
  coop_salesperson_1: undefined,
  coop_salesperson_2: undefined,
  condition: undefined,
  condition_expiry: undefined,
};

const baseRequiredFields: (keyof ListingFormState)[] = [
  'type',
  'subtype',
  'line1',
  'country',
  'province',
  'city',
  'neighborhood',
  'postal_code',
  'list_price',
  'expiry',
  'status',
];

const errorMessages = {
  generic:
    'In order to save your listing, you must fill out all mandatory fields.',
  openHouses:
    'In order to save your listing, you must fix the errors in the open house fields below.',
  zeroPrice:
    'In order to save your listing, you must fill out a price greater than zero.',
  comingSoonGreaterThanExpiry:
    'In order to save your listing, the "Coming Soon Date" must be earlier than or equal to the "Expiry Date".',
};
const informativeMessages = {
  incompleteListing:
    'You can save this listing with missing fields as the required data has not been imported into our system. Once all of the required information has been imported, you can no longer leave a required field blank',
};

const useListing: UseListingType = listingId => {
  const { auth } = React.useContext(AuthContext);
  const isMounted = React.useRef(true);
  const history = useHistory();
  const [initialState, setInitialState] = React.useState<{
    listing: ListingFormState;
    photos: PhotoFileType[];
    attachments: Record<string, PhotoFileType>;
  }>({
    listing: emptyListing,
    photos: [],
    attachments: {},
  });
  const [documents, setDocuments] = React.useState([]);
  const [listing, setListing] = React.useState(emptyListing);
  const [features, setFeatures] = React.useState<OrgFeatures[]>();
  const [loading, setLoading] = React.useState(true);
  const [listingCreated, setListingCreated] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [formFieldsMissing, setFormFieldsMissing] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [availabilityDetails, setAvailabilityDetails] = React.useState(
    initialAvailabilityDetails,
  );
  const [validAvailabilities, setValidAvailabilities] = React.useState<
    ListingType['availability'][]
  >([]);

  const [photos, setPhotos] = React.useState<PhotoFileType[]>([]);
  // A Separate state for deleted photos are needed since grid sorting uses array index
  // and we are passing only non-deleted photos, which will mess up the indexing
  const [deletedPhotos, setDeletedPhotos] = React.useState<PhotoFileType[]>([]);
  const [thumbnail, setThumbnail] = React.useState<PhotoFileType | null>(null);
  // Attachments are same type in FE as photos, but they are unordered and not limited to image type
  const [attachments, setAttachments] = useImmer<Record<string, PhotoFileType>>(
    {},
  );

  const [openHousesErrors, setOpenHousesErrors] = React.useState<boolean[]>([]);
  const [isListingDeleted, setIsListingDeleted] =
    React.useState<boolean>(false);
  const [isListingRestored, setIsListingRestored] =
    React.useState<boolean>(false);
  const [hasNarSettlement, setHasNarSettlement] = React.useState(false);
  const [hasNarBuyerSettlement, setHasNarBuyerSettlement] =
    React.useState(false);

  const uploading = React.useMemo(() => {
    const uploadingAttachments = Object.values(attachments).some(
      ({ url }) => !url,
    );
    const uploadingPhotos = photos.some(({ url }) => !url);
    return uploadingAttachments || uploadingPhotos;
  }, [attachments, photos]);

  const { getAvailability, getLabel } = React.useContext(ListingLabelContext);

  const activeAttachments = React.useMemo(
    () =>
      Object.entries(attachments)
        .filter(([, { isDeleted }]) => !isDeleted)
        .map(([uid, attachment]) => ({
          ...attachment,
          uid,
        }))
        .sort((a, b) => {
          if (!a.updatedAt) return -1;
          if (!b.updatedAt) return 1;
          return isAfter(new Date(b.updatedAt), new Date(a.updatedAt)) ? -1 : 1;
        }),
    [attachments],
  );

  const isPhotoModified = React.useMemo(
    () =>
      !isEqual(initialState.photos, photos) ||
      initialState.listing.thumbnail !== thumbnail?.url,
    [initialState, photos, thumbnail],
  );

  const isAttachmentModified = React.useMemo(
    () => !isEqual(initialState.attachments, attachments),
    [initialState, attachments],
  );

  const isListingModified = React.useMemo(
    () =>
      // Do shallow comparison for listing state,
      initialState.listing !== listing ||
      isPhotoModified ||
      isAttachmentModified,
    [initialState, listing, isPhotoModified, isAttachmentModified],
  );

  const isListingDeletedOrRestored = React.useMemo(
    () => isListingDeleted || isListingRestored,
    [isListingDeleted, isListingRestored],
  );

  const updateFile = (fileId: string, file: PhotoFileType) => {
    setAttachments(draft => {
      draft[fileId] = file;
      // Error on FE for an attachment that isn't saved in BE should remove it from state when removing the file
      if (file.status === 'removed' && file.isDeleted && !file.id)
        delete draft[fileId];
    });
  };

  const getOpenHousesErrors = () =>
    listing.openHouses.map(({ date, timeFrom, timeTo }) => {
      // Allow invalid OHs if the org setting is disabled
      // Should not be possible to have an invalid OH after KUSA-60 but the BE will filter them out
      if (!auth?.org.settings.enableOpenHouses) return false;
      if (!date || !timeFrom || !timeTo) return true;
      return !isAfter(new Date(timeTo), new Date(timeFrom));
    });

  const getError = useCallback(() => {
    const check = (input: any) => Boolean(input || input === 0);

    const filledBasicRequiredFields =
      baseRequiredFields.every(field => check(listing[field])) &&
      listing.reps?.length > 0;
    if (!filledBasicRequiredFields) return errorMessages.generic;

    if (listing.type === 'commercial' && !check(listing.list_price_type)) {
      return errorMessages.generic;
    }

    if (listing.status === 'Coming Soon' && !check(listing.coming_soon_date)) {
      return errorMessages.generic;
    }

    if (getOpenHousesErrors().some(Boolean)) {
      return errorMessages.openHouses;
    }

    if (availabilityDetails.price === 0) return errorMessages.zeroPrice;

    if (listing.status === 'Coming Soon') {
      const comingSoonDate = moment(listing.coming_soon_date)
        .startOf('day')
        .unix();
      const expiryDate = moment(listing.expiry).startOf('day').unix();

      if (comingSoonDate > expiryDate) {
        return errorMessages.comingSoonGreaterThanExpiry;
      }
    }

    const {
      newAvailability,
      suspended_date,
      price,
      sold_date,
      leased_date,
      leased_term,
      closed_date,
      coop_brokerage,
      coop_salesperson_1,
      condition,
      condition_expiry,
    } = availabilityDetails;

    switch (newAvailability) {
      case 'Price Change':
        if (!check(price)) return errorMessages.generic;
        break;
      case 'Suspended':
        if (!suspended_date) return errorMessages.generic;
        break;
      case 'Sold':
        if (
          [
            price,
            sold_date,
            closed_date,
            coop_brokerage,
            coop_salesperson_1,
          ].some(value => !check(value))
        ) {
          return errorMessages.generic;
        }
        break;
      case 'Leased':
        if (
          [
            price,
            leased_date,
            closed_date,
            coop_brokerage,
            coop_salesperson_1,
            leased_term,
          ].some(value => !check(value))
        ) {
          return errorMessages.generic;
        }
        break;
      case 'Sold Conditionally':
      case 'Leased Conditionally':
        if ([condition, condition_expiry].some(value => !value)) {
          return errorMessages.generic;
        }
        break;
      default:
        break;
    }
  }, [listing, availabilityDetails]);

  const isFormValid = React.useMemo(() => !getError(), [listing]);

  const checkDuplicateOrMissingIdInRooms = (
    rooms: ListingFormState['rooms'],
  ) => {
    const ids = rooms.map(room => room.id);
    const uniqueIds = new Set(ids);
    const hasDuplicateIds = uniqueIds.size !== ids.length;
    const hasMissingIds = ids[ids.length - 1] !== ids.length || ids[0] !== 1;
    return hasDuplicateIds || hasMissingIds;
  };

  const formatListingToSave = () => {
    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { expiry, coming_soon_date, timezone = localTimezone } = listing;
    const { neighborhood, ...listingToSave } = listing;
    const displayedField =
      getLabel('Neighborhood Display')?.trim().toUpperCase() === 'COUNTY'
        ? 'county'
        : 'neighborhood';

    const roomsWithContent: ListingFormState['rooms'] = listing.rooms.filter(
      room => Object.values({ ...room, id: null }).some(Boolean),
    );

    if (
      roomsWithContent.length &&
      checkDuplicateOrMissingIdInRooms(roomsWithContent)
    ) {
      for (let i = 0; i < roomsWithContent.length; i++) {
        roomsWithContent[i].id = i + 1;
      }
    }

    return {
      ...listingToSave,
      [displayedField]: neighborhood,
      thumbnail: thumbnail?.url || null,
      expiry: expiry.toISOString(),
      coming_soon_date: coming_soon_date
        ? coming_soon_date.toISOString()
        : undefined,
      rooms: roomsWithContent,
      openHouses: listing.openHouses
        .filter(({ date, timeFrom, timeTo }) => date && timeFrom && timeTo)
        .map(({ date, ...openHouse }) => ({
          ...openHouse,
          timeTo: zonedTimeToUtc(openHouse.timeTo, timezone).toISOString(),
          timeFrom: zonedTimeToUtc(openHouse.timeFrom, timezone).toISOString(),
        })),
    };
  };

  const formatListingAsForm = (rawListing: ListingType): ListingFormState => {
    const { expiry, coming_soon_date, timezone, county, neighborhood } =
      rawListing;
    const displayedField =
      getLabel('Neighborhood Display')?.trim().toUpperCase() === 'COUNTY'
        ? county
        : neighborhood;

    return {
      ...emptyListing,
      ...rawListing,
      neighborhood: displayedField,
      expiry: new Date(expiry),
      coming_soon_date: coming_soon_date
        ? new Date(coming_soon_date)
        : undefined,
      openHouses: rawListing.openHouses.map(openHouse => {
        const date = isValid(new Date(openHouse.date))
          ? parseISO(openHouse.date as string)
          : utcToZonedTime(startOfDay(new Date(openHouse.timeFrom)), timezone);
        const timeFrom = isValid(new Date(openHouse.timeFrom))
          ? utcToZonedTime(openHouse.timeFrom, timezone)
          : parseDate(openHouse.timeFrom, 'hh:mm a', new Date(openHouse.date));
        const timeTo = isValid(new Date(openHouse.timeTo))
          ? utcToZonedTime(openHouse.timeTo, timezone)
          : parseDate(openHouse.timeTo, 'hh:mm a', new Date(openHouse.date));
        return {
          ...openHouse,
          date,
          timeFrom,
          timeTo,
        };
      }),
    };
  };

  const formatPhotosToSave = React.useCallback(() => {
    let order = 0;
    // filtering images only
    const newPhotos = photos
      .filter(photo => photo.mimetype.includes('image'))
      .map(photo => {
        if (photo.isDeleted) return photo;

        const orderedPhoto: PhotoFileType & {
          isThumbnail?: boolean;
          order?: number;
        } = {
          ...photo,
          order,
        };
        order += 1;

        if (thumbnail?.url === photo.url) {
          orderedPhoto.isThumbnail = true;
        }

        return orderedPhoto;
      });
    return [...newPhotos, ...deletedPhotos];
  }, [photos, deletedPhotos, thumbnail]);

  const formatFilesAsEditable = (rawFiles: GetListingFilesResponse) => {
    const rawPhotos = sortBy(
      rawFiles.filter(file => file.isPhoto),
      ['order', 'name'],
    );
    const rawAttachments = sortBy(
      rawFiles.filter(file => file.isAttachment),
      'updatedAt',
    );

    const formattedPhotos: PhotoFileType[] = rawPhotos.map(rawPhoto => ({
      id: rawPhoto.id,
      url: rawPhoto.url,
      mimetype: rawPhoto.filetype,
      filename: rawPhoto.name,
      updatedAt: rawPhoto.updatedAt,
      status: 'done',
    }));
    const formattedAttachments = rawAttachments.reduce(
      (acc, cur) => {
        acc[cur.id] = {
          id: cur.id,
          url: cur.url,
          mimetype: cur.filetype,
          filename: cur.name,
          size: cur.size,
          updatedAt: cur.updatedAt,
          status: 'done',
          order: cur.order, // if attachment has order then it was uploaded as an image
        } as PhotoFileType;
        return acc;
      },
      {} as Record<string, PhotoFileType>,
    );

    return { formattedPhotos, formattedAttachments };
  };

  const showSuccessMessage = (
    visibilityOverride?: ListingFormState['visibility'],
  ) => {
    const editing = Boolean(listingId);
    const visibility = visibilityOverride || listing.visibility;

    switch (visibility) {
      case 'brokerage':
        if (editing) {
          if (initialState.listing.visibility === 'private') {
            message.success(
              'Your listing has been updated and shared to your brokerage.',
            );
          } else {
            message.success('Your listing has been updated.');
          }
        } else {
          message.success(
            'Your listing has been created and shared to your brokerage.',
          );
        }
        break;
      case 'network': // Only available when creating listing (!editing)
        message.success(
          'Your listing has been updated and shared to the BrokerBay Exclusive Network.',
        );
        break;
      default:
        message.success('Your listing has been saved as a draft.');
        break;
    }
  };

  const refetchFiles = async (newId?: string) => {
    const { data: currentFiles } = await axios.get<GetListingFilesResponse>(
      `/listing/${newId || listing.id}/file`,
    );
    const { formattedPhotos, formattedAttachments } =
      formatFilesAsEditable(currentFiles);
    if (!isMounted.current) return;

    setInitialState(prev => ({
      ...prev,
      photos: formattedPhotos,
      attachments: formattedAttachments,
    }));
    setAttachments(draft =>
      Object.entries(formattedAttachments).reduce(
        (acc: Record<string, PhotoFileType>, [fileId, file]) => {
          acc[fileId] = file;
          return acc;
        },
        {},
      ),
    );
    setPhotos(formattedPhotos);
  };

  const saveListing: SaveListingType = async options => {
    const { forceDraft } = options || {};

    const error = getError();

    setOpenHousesErrors(getOpenHousesErrors());

    if (error) {
      // We still want to display other messages (i.e. zeroPrice and openHouses)
      if (listing.isIncomplete && error !== errorMessages.generic) {
        setErrorMessage(error);
      }
      if (!listing.isIncomplete) {
        setErrorMessage(error);
        if (error === errorMessages.generic) {
          setFormFieldsMissing(true);
        }
        return false;
      }
    }
    setFormFieldsMissing(false);

    try {
      setLoading(true);
      const formattedListing = formatListingToSave();
      if (forceDraft) {
        formattedListing.visibility = 'private';
      }
      const formattedPhotos = isPhotoModified ? formatPhotosToSave() : null;
      const formattedAttachments = isAttachmentModified
        ? Object.values(attachments)
        : null;

      const { newAvailability } = availabilityDetails;
      const availabilityChange =
        newAvailability && validAvailabilities.includes(newAvailability)
          ? availabilityDetails
          : null;

      if (availabilityChange) {
        const swalTitle =
          newAvailability === 'Price Change' ? 'Price' : 'Status';
        const swalText =
          newAvailability === 'Price Change'
            ? formatCurrency(availabilityDetails.price)
            : getAvailability(newAvailability);
        const { value } = await swal.fire({
          title: `Change ${swalTitle}`,
          text: `Are you sure you want to change the ${swalTitle.toLowerCase()} of ${
            formattedListing.line1
          } to ${swalText}?`,
          type: 'warning',
          showCancelButton: true,
          confirmButtonColor: theme.green7,
          confirmButtonText: `Change ${swalTitle}`,
          closeButtonHtml: 'Cancel',
        });
        if (!value) {
          setLoading(false);
          return false;
        }
      }

      if (!auth?.isAdmin && !listingId) {
        formattedListing.documentStatus = 'uploaded';
      }

      const postUrl = listing.id ? `/listing/${listing.id}` : '/listing';
      const { data: createdListing } = await axios.post<Listing>(postUrl, {
        listing: formattedListing,
        photos: formattedPhotos,
        attachments: formattedAttachments,
        availabilityChange,
        documents,
      });

      if (!isMounted.current) return;
      if (forceDraft) {
        await refetchFiles(createdListing.id);
      }
      setListing(prev => ({
        ...prev,
        id: createdListing.id,
        pageStatus: createdListing.pageStatus,
      }));
      setSaved(true);
      setListingCreated(true);
      setLoading(false);

      showSuccessMessage(listing.visibility);

      return true;
    } catch (err) {
      errorReporter.error(err);
      message.error('Error saving listing.');
      if (isMounted.current) setLoading(false);

      return false;
    }
  };

  const distributeListing = async () => {
    try {
      if (!listing?.id) return;
      setLoading(true);
      await axios.post(`/listing/${listing.id}/distribute`, {
        visibility: listing.visibility,
        pageStatus: listing.pageStatus,
      });
      history.push(`/listing/${listing.id}/view`);

      showSuccessMessage();
    } catch (err) {
      errorReporter.error(err);
      message.error('Error paging listing.');
    } finally {
      setLoading(false);
    }
  };
  const submitListingForReview = async () => {
    try {
      if (!listing?.id) return;
      setLoading(true);
      const visibility =
        listing.visibility === 'pending'
          ? listing.pendingVisibility
          : listing.visibility;
      await axios.post(`/listing/${listing.id}/submit`, {
        pendingVisibility: visibility,
      });
      message.success('Your listing has been submitted for review');
      history.push(`/listing/${listing.id}/view`);
    } catch (err) {
      errorReporter.error(err);
      message.error('Error submitting listing.');
    } finally {
      setLoading(false);
    }
  };
  const deleteListing = async () => {
    try {
      if (listing.isDeleted) {
        throw new Error('Cannot delete an already deleted listing');
      }
      const deleteId = listing.id || listingId;
      if (!deleteId) {
        throw new Error('Cannot delete a listing without listingId');
      }

      await axios.post(`/listing/delete/${deleteId}`);
      setIsListingDeleted(true);
    } catch (err) {
      errorReporter.error(err);
      message.error('Error deleting a listing.');
    }
  };

  const restoreListing = async () => {
    try {
      if (!listing.isDeleted) {
        throw new Error('Cannot restore a non-deleted listing');
      }
      const restoreId = listing.id || listingId;
      if (!restoreId) {
        throw new Error('Cannot restore a listing without listingId');
      }

      await axios.post(`/listing/${restoreId}/restore`);
      setIsListingRestored(true);
    } catch (err) {
      errorReporter.error(err);
      message.error('Error restoring a listing.');
    }
  };

  const fetchListing = async () => {
    try {
      setLoading(true);
      const [
        { data: currentListing },
        { data: currentFiles },
        { data: currentAvailabilities },
      ] = await Promise.all([
        axios.get<ListingType>(`/listing/${listingId}`),
        axios.get<
          BuildJsonType<
            (FileDoc & { isPhoto: boolean; isAttachment: boolean })[]
          >
        >(`/listing/${listingId}/file`),
        axios.get<ListingType['availability'][]>(
          `/statuses/valid/${listingId}`,
        ),
      ]);

      if (featureEnabled('narSettlementFeature')) {
        const portalId = currentListing.org?.onboardingPortalId;
        const orgId = currentListing.org?.id;

        if (!portalId || !orgId) {
          throw new Error(
            `Cannot fetch NAR Settlement Values. Received portalId: ${portalId}, orgId: ${orgId}`,
          );
        }
        await fetchHasNarSettlementValue({ portalId, orgId });
      }

      if (currentListing.isIncomplete) {
        // Banner display to let user know they can save without required fields
        setErrorMessage(informativeMessages.incompleteListing);
      }

      setFeatures(currentListing.org.features);
      const formattedListing = formatListingAsForm(currentListing);
      const { formattedPhotos, formattedAttachments } =
        formatFilesAsEditable(currentFiles);

      setInitialState({
        listing: formattedListing,
        photos: formattedPhotos,
        attachments: formattedAttachments,
      });
      setListing(formattedListing);
      setPhotos(formattedPhotos);
      if (formattedListing.thumbnail) {
        setThumbnail({
          url: formattedListing.thumbnail,
          mimetype: 'image/png',
          filename: `listing_thumbnail_${listingId}`,
          status: 'done',
        });
      }
      setAttachments(draft => {
        Object.entries(formattedAttachments).forEach(([fileId, file]) => {
          draft[fileId] = file;
        });
      });

      setValidAvailabilities(
        currentAvailabilities as ListingType['availability'][],
      );
    } catch (err) {
      errorReporter.error(err);
      message.error('Error loading listing edit page.');
    } finally {
      setLoading(false);
    }
  };

  const hasFeature = useCallback(
    (feature: OrgFeatures) => {
      const isAssumed = Boolean(auth?.assumingUserId || auth?.isAssumedNotBB);
      if (isAssumed && auth?.org.id === listing.org?.id) return true;

      return Boolean(features?.includes(feature));
    },
    [auth, listing, features],
  );

  const fetchHasNarSettlementValue = async ({
    portalId,
    orgId,
  }: {
    portalId: string;
    orgId: string;
  }) => {
    const associationData = await getOnboardingPortalAssociationByPortalId({
      portalId,
      orgId,
    });
    const narSettlementValue = associationData?.narSettlementCommission;
    const narBuyerSettlementValue = associationData?.narSettlementAgreement;
    setHasNarSettlement(Boolean(narSettlementValue));
    setHasNarBuyerSettlement(Boolean(narBuyerSettlementValue));
  };

  React.useEffect(() => {
    if (listingId) {
      fetchListing();
    } else {
      const portalId = String(auth?.org.onboardingPortalId);
      const orgId = auth?.org.id;
      if (featureEnabled('narSettlementFeature') && portalId && orgId) {
        fetchHasNarSettlementValue({ portalId, orgId });
      }
      setLoading(false);
    }
  }, [listingId, getLabel]);

  React.useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  React.useEffect(() => {
    if (isListingDeleted) {
      history.push('/listing/list/brokerage/0');
    } else if (isListingRestored) {
      history.push('/');
    }
  }, [history, isListingDeleted, isListingRestored]);

  React.useEffect(() => {
    setOpenHousesErrors(getOpenHousesErrors());
  }, [listing.openHouses]);

  React.useEffect(() => {
    if (!getError()) {
      setErrorMessage('');
    }
  }, [listing, getError]);

  const handleSetListing = React.useCallback<
    React.Dispatch<React.SetStateAction<ListingFormState>>
  >(callback => {
    setSaved(false);
    setListing(callback);
  }, []);

  return {
    listing,
    setListing: handleSetListing,
    initialListing: initialState.listing,
    photos,
    setPhotos,
    setDeletedPhotos,
    thumbnail,
    setThumbnail,
    attachments: activeAttachments,
    updateFile,
    isListingModified,
    isListingDeletedOrRestored,
    loading,
    uploading,
    saveListing,
    distributeListing,
    submitListingForReview,
    deleteListing,
    restoreListing,
    saved,
    listingCreated,
    isFormValid,
    errorMessage,
    formFieldsMissing,
    openHousesErrors,
    validAvailabilities,
    availabilityDetails,
    setAvailabilityDetails: callback => {
      setSaved(false);
      setErrorMessage('');
      setAvailabilityDetails(callback);
    },
    initialAvailabilityDetails,
    documents,
    setDocuments,
    hasFeature,
    hasNarSettlement,
    hasNarBuyerSettlement,
  };
};

export default useListing;
